import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useSound } from 'use-sound';

const FakeAntivirus = () => {
  const [scanState, setScanState] = useState<'idle' | 'scanning' | 'complete'>('idle');
  const [detectedFiles, setDetectedFiles] = useState<string[]>([]);
  const [progress, setProgress] = useState(0);
  const [playAlert] = useSound('/sounds/win32.mp3');
  const [playError] = useSound('./sounds/blue-screen.mp3');

  const fakeFiles = [
    'cringe_selfies_2015.zip',
    'secret_cat_memes_vault.iso',
    'embarrassing_dance_videos.mov',
    'failed_projects_backup.tar',
    'awkward_chat_logs_archive.rar',
    'unfinished_side_projects.bin'
  ];

  const startScan = () => {
    setScanState('scanning');
    setProgress(0);
    setDetectedFiles([]);
  };

  useEffect(() => {
    if (scanState === 'scanning') {
      const scanInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            clearInterval(scanInterval);
            setScanState('complete');
            playAlert();
            return 100;
          }
          
          // Add random file detection
          if (Math.random() < 0.3 && detectedFiles.length < fakeFiles.length) {
            setDetectedFiles(prev => [...prev, fakeFiles[prev.length]]);
            playError();
          }
          
          return Math.min(prev + Math.random() * 4, 100);
        });
      }, 200);

      return () => clearInterval(scanInterval);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanState, detectedFiles.length]);

  return (
    <div className="w-full h-full lg:w-[1100px] lg:h-[600px] bg-gray-900 text-green-400 p-8 font-mono overflow-hidden relative">
      {/* Scan line animation */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="h-px w-full bg-green-500/20 animate-scan-line" />
      </div>

      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8 border-b border-green-500/30 pb-4">
          <div className="p-4 bg-red-600 rounded-full animate-pulse">
            <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <div>
            <h1 className="text-3xl font-bold text-red-500">LACKSHIELD ULTRA</h1>
            <p className="text-green-500/70">Advanced Threat Protection System</p>
          </div>
        </div>

        {/* Scan Controls */}
        {scanState === 'idle' && (
          <div className="text-center py-12">
            <button
              onClick={startScan}
              className="px-8 py-3 bg-red-600 hover:bg-red-700 rounded-lg text-xl 
                       font-bold text-white transition-all hover:scale-105"
            >
              🛡️ Start Full System Scan
            </button>
            <p className="mt-4 text-green-500/50 text-sm">
              Last scan: 69420 threats detected • System status: CRITICAL
            </p>
          </div>
        )}

        {/* Scanning Interface */}
        {scanState === 'scanning' && (
          <div className="space-y-6">
            <div className="bg-black/30 p-4 rounded-lg border border-green-500/20">
              <div className="flex justify-between mb-2">
                <span>Scanning system files...</span>
                <span>{Math.floor(progress)}%</span>
              </div>
              <div className="h-2 bg-green-900 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-red-500 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {detectedFiles.map((file, index) => (
                <div key={index} className="p-3 bg-black/40 rounded-lg border border-red-500/30 animate-fade-in">
                  <div className="flex items-center gap-2">
                    <span className="text-red-500">⚠️</span>
                    <span className="truncate">{file}</span>
                    <span className="text-red-500/50 text-sm ml-auto">HIGH RISK</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Scan Results */}
        {scanState === 'complete' && (
          <div className="space-y-6 animate-fade-in">
            <div className="p-6 bg-red-900/30 border border-red-500 rounded-lg">
              <h2 className="text-2xl text-red-500 mb-4 flex items-center gap-2">
                <span>🚨 CRITICAL THREATS DETECTED!</span>
              </h2>
              
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div className="p-3 bg-black/40 rounded">
                    <p className="text-red-500">Severity Level</p>
                    <p className="text-2xl">EXTREME</p>
                  </div>
                  <div className="p-3 bg-black/40 rounded">
                    <p className="text-red-500">Files Scanned</p>
                    <p className="text-2xl">42,069</p>
                  </div>
                  <div className="p-3 bg-black/40 rounded">
                    <p className="text-red-500">Threats Found</p>
                    <p className="text-2xl">666</p>
                  </div>
                </div>

                <div className="p-4 bg-black/40 rounded border border-red-500/30">
                  <p className="text-red-500 mb-2">Recommended action:</p>
                  <p className="text-lg">Immediate system purge recommended</p>
                </div>
              </div>
            </div>

            <div className="flex gap-4 justify-center">
              <button
                onClick={() => toast('⚠️ Purge incomplete, but your computer has 69420 viruses, download more ram at www.scammy-antivirus.com')}
                className="px-6 py-3 bg-red-600 hover:bg-red-700 rounded-lg 
                         text-white flex items-center gap-2 transition-transform hover:scale-105"
              >
                <span>🔥</span>
                Purge All Threats
              </button>
              <button
                onClick={() => setScanState('idle')}
                className="px-6 py-3 bg-green-900/30 hover:bg-green-900/50 rounded-lg 
                         border border-green-500/30 transition-all"
              >
                Close Scanner
              </button>
            </div>
          </div>
        )}

        {/* System Tray */}
        <div className="fixed bottom-0 left-0 right-0 bg-black/50 border-t border-green-500/20 p-2">
          <div className="flex items-center gap-4 text-sm">
            <span className="text-red-500 animate-pulse">● LIVE PROTECTION: OFF</span>
            <span>|</span>
            <span>Last Update: 01-06-1970</span>
            <span>|</span>
            <span className="text-yellow-500">Subscription: EXPIRED</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FakeAntivirus;